import axios, { AxiosError } from "axios";
import apiClient from "@/lib/axios";
import { K, getOrCreateDeviceId } from "./token";

export * from "./token";

const BASE = import.meta.env.VITE_API_BASE_URL || "https://api.travelhug.ai/";






// --- User Auth Types & Functions ---

// Matches AuthUser from OpenAPI
export type User = {
    email: string;
    email_verified: boolean;
    id: string;
    first_name?: string;
    last_name?: string;
    username?: string;
    role?: string;
    profile_photo_url?: string;
    is_active?: boolean;
    phone_number?: string;
    gender?: string;
    country?: string;
    address_line_1?: string;
    address_line_2?: string;
    travel_preferences?: string;
    tour_style?: string;
    interests?: string[];
    dietary_restrictions?: string[];
    cuisine_preferences?: string[];
    avoid_foods?: string[];
    accessibility_needs?: string[];
};

// Matches UserCreate from OpenAPI
export type RegisterPayload = {
    email: string;
    // Docs say these are optional/nullable, but our UI enforces them.
    first_name: string;
    last_name: string;
    username?: string; // Made optional
    password: string;
    confirm_password: string;
};

export type LoginPayload = {
    identifier: string; // username or email
    password: string;
};

export type VerifyEmailPayload = {
    email: string;
    code: string;
};

export type AuthResponse = {
    access_token: string;
    expires_at: number;
    refresh_token: string;
    user: User;
};

export async function register(payload: RegisterPayload) {
    const res = await axios.post(`${BASE}api/v1/auth/register`, payload, {
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
        },
    });
    return res.data;
}

export async function login(payload: LoginPayload) {
    const res = await axios.post(`${BASE}api/v1/auth/login`, payload, {
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-Device-Id": getOrCreateDeviceId(),
        },
    });

    const { access_token, expires_at, refresh_token } = res.data;

    if (access_token) {
        localStorage.setItem(K.token, access_token);
        if (refresh_token) localStorage.setItem(K.refreshToken, refresh_token);
        if (expires_at) {
            const expTime = typeof expires_at === "number" ? expires_at * 1000 : expires_at;
            localStorage.setItem(K.exp, new Date(expTime).toISOString());
        }
    }

    return res.data as AuthResponse;
}

export async function verifyEmail(payload: VerifyEmailPayload) {
    const res = await axios.post(`${BASE}api/v1/auth/verify-email`, payload, {
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
        },
    });
    return res.data;
}

export async function resendEmailVerification(email: string) {
    const res = await axios.post(`${BASE}api/v1/auth/resend-email-verification`, { email }, {
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
        },
    });
    return res.data;
}


export type ResetPasswordPayload = {
    email: string;
    code: string;
    new_password: string; // Changed from password to match API
    confirm_password: string;
};

export type ChangePasswordPayload = {
    current_password: string;
    new_password: string;
    confirm_password: string;
};

export async function forgotPassword(email: string) {
    const res = await axios.post(`${BASE}api/v1/auth/forgot-password`, { email }, {
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-Device-Id": getOrCreateDeviceId(),
        },
    });
    return res.data;
}

export async function resetPassword(payload: ResetPasswordPayload) {
    // API expects: email, code, new_password, confirm_password
    const res = await axios.post(`${BASE}api/v1/auth/reset-password`, payload, {
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-Device-Id": getOrCreateDeviceId(),
        },
    });
    return res.data;
}


export async function changePassword(payload: ChangePasswordPayload) {
    const token = localStorage.getItem(K.token);
    const res = await axios.post(`${BASE}api/v1/auth/change-password`, payload, {
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "Authorization": `Bearer ${token}`,
            "X-Device-Id": getOrCreateDeviceId(),
        },
    });
    return res.data;
}

export async function getMe() {
    // We check purely for existence of token to decide if we even attempt the call.
    // However, apiClient handles the actual Authorization header and refresh logic.
    const token = localStorage.getItem(K.token);
    if (!token) return null;

    // Use apiClient so that interceptors run (attaching token, handling 401 refresh)
    const res = await apiClient.get<User>("api/v1/users/me");
    return res.data;
}
